import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getOnboardingChecklist } from "../api/onboardingApi";
import type { OnboardingChecklistStepOut } from "../api/types";
import { useAsyncData } from "../lib/useAsyncData";
import { StatusBanner } from "./StatusBanner";

/** Collapsed/expanded persists like `useNavMode`'s `sl-dashboard-nav-mode` — once an owner
 * has looked at the full checklist (or dismissed it back down), that choice should survive
 * a reload instead of re-covering the dashboard every visit. Starts collapsed: the one-liner
 * is the "is there anything left" glance; expanding is an opt-in. */
const EXPANDED_KEY = "sl-onboarding-expanded";

/** Per-step instructions/links this app owns (the backend only knows `label`/`completed`/
 * `current`/`target` — it has no idea `dashboard-admin`'s stock page lives at `/stock`). */
const STEP_COPY: Record<string, { instruction: string; to?: string }> = {
  BUSINESS_CREATED: { instruction: "Your business is live on ZEL-MART." },
  PRODUCTS_STOCKED: {
    instruction: "Add products with clear photos so customers and staff can find them.",
    to: "/stock",
  },
  POS_SALES_MADE: {
    instruction: "Ring up a sale at the till to try the point of sale.",
    to: "/pos/till",
  },
  ONLINE_SALES_MADE: {
    instruction: "Turn on your online store and share the link to get your first online orders.",
    to: "/system?tab=branding",
  },
  BRANDING_CUSTOMIZED: {
    instruction: "Add your logo so your storefront and receipts look like your business.",
    to: "/system?tab=branding",
  },
  STAFF_ADDED: {
    instruction: "Invite a staff member so you're not the only one who can run the till.",
    to: "/staff",
  },
};

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
      <path d="M5 12.5 10 17 19 7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      aria-hidden="true"
      className={`sl-onboarding__chevron${expanded ? " sl-onboarding__chevron--up" : ""}`}
    >
      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChecklistStep({ step, index }: { step: OnboardingChecklistStepOut; index: number }) {
  const copy = STEP_COPY[step.step];
  const progress = step.target != null ? ` (${Math.min(step.current ?? 0, step.target)}/${step.target})` : "";

  return (
    <li className={`sl-onboarding__step${step.completed ? " sl-onboarding__step--done" : ""}`}>
      <span className="sl-onboarding__marker">{step.completed ? <CheckIcon /> : index + 1}</span>
      <div className="sl-onboarding__step-body">
        <div className="sl-onboarding__step-label">
          {step.label}
          {progress}
        </div>
        {copy ? <p className="sl-onboarding__step-copy">{copy.instruction}</p> : null}
      </div>
      {!step.completed && copy?.to ? (
        <Link to={copy.to} className="sl-button sl-button--secondary sl-button--md sl-onboarding__cta">
          Go
        </Link>
      ) : null}
    </li>
  );
}

/**
 * The dashboard-admin "getting started" widget (§9.2's onboarding checklist, expanded from
 * 3 steps to 6 real milestones — business created, 10 products with photos, 5 POS sales, 3
 * online sales, branding/logo, one extra staff member). Same slot `OnlineStoreToggle`/
 * `StatusBanner` already occupy on `DashboardPage`. Renders nothing once `all_complete` —
 * a new business needs the nudge, a fully set-up one doesn't need a permanent trophy case —
 * and only for OWNER, the one role that can act on every step (staff/branding routes are
 * already OWNER-gated).
 */
export function OnboardingChecklist() {
  const { user, ensureFreshToken } = useAuth();
  const isOwner = user?.role === "OWNER";
  const state = useAsyncData(
    () => (isOwner ? ensureFreshToken().then((token) => getOnboardingChecklist(token)) : Promise.resolve(null)),
    [isOwner],
  );
  const [expanded, setExpanded] = useState<boolean>(() => localStorage.getItem(EXPANDED_KEY) === "1");

  if (!isOwner || state.loading || !state.data || state.data.all_complete) {
    return state.error ? <StatusBanner kind="error">{state.error}</StatusBanner> : null;
  }

  const { steps } = state.data;
  const completedCount = steps.filter((s) => s.completed).length;
  const pct = Math.round((completedCount / steps.length) * 100);

  function toggleExpanded() {
    setExpanded((current) => {
      const next = !current;
      localStorage.setItem(EXPANDED_KEY, next ? "1" : "0");
      return next;
    });
  }

  return (
    <div className={`sl-onboarding${expanded ? "" : " sl-onboarding--collapsed"}`}>
      <button
        type="button"
        className="sl-onboarding__toggle"
        onClick={toggleExpanded}
        aria-expanded={expanded}
        aria-controls="sl-onboarding-body"
      >
        <span className="sl-onboarding__toggle-title">Get your business ready</span>
        <span className="sl-onboarding__progress-track sl-onboarding__progress-track--inline">
          <span className="sl-onboarding__progress-fill" style={{ width: `${pct}%` }} />
        </span>
        <span className="sl-onboarding__progress-label">
          {completedCount} of {steps.length} done
        </span>
        <ChevronIcon expanded={expanded} />
      </button>
      {expanded ? (
        <div id="sl-onboarding-body" className="sl-onboarding__body">
          <p className="sl-onboarding__subtitle">
            {completedCount === 0
              ? "A few quick steps and you'll be ready for business."
              : "Great progress — keep going to unlock the full picture of your business."}
          </p>
          <ol className="sl-onboarding__steps">
            {steps.map((step, index) => (
              <ChecklistStep key={step.step} step={step} index={index} />
            ))}
          </ol>
        </div>
      ) : null}
    </div>
  );
}
